import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCarePlanInstance,
  activeCarePlanDetailView,
  createDraftCarePlan,
  deleteCarePlan,
} from "../../../redux/actions/careplanActions";
import { clearCarePlan } from "../../../redux/slices/careplanSlice";

import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import FloatingMenu from "../../common/FloatingMenu";
import TextField from "../../common/TextField";
import UrgentCare from "./UrgentCare";
import Spacer from "../../common/Spacer";
import { CreateDraftIcon, DeleteIcon, PublishIcon } from "../../common/Icons";
import ItemThumbnail from "../../common/ItemThumbnail";
import DraftCreationStatus from "../../common/popups/DraftCreationStatus";

const ActivePlanAdminView = ({ carePlanId, onClose }) => {
  const dispatch = useDispatch();
  const {
    sections = [],
    contacts = [],
    nhsNumber,
    important_info,
    public_id,
  } = useSelector((state) => state.careplan.carePlan);

  // States for draft creation, deletion
  const [showCreateDraftConfirm, setShowCreateDraftConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDraftStatus, setShowDraftStatus] = useState(false);
  const [draftStatus, setDraftStatus] = useState("success");
  const [deleteText, setDeleteText] = useState("");

  // useGoogleTranslate();

  useEffect(() => {
    dispatch(clearCarePlan()); // Clear carePlan on Component Mount
    dispatch(fetchCarePlanInstance(carePlanId));
    dispatch(activeCarePlanDetailView(carePlanId));
  }, [carePlanId, dispatch]);

  // If care plan hasn't loaded yet
  if (!nhsNumber) {
    return <div>Loading...</div>;
  }

  // Actions
  const handlePrint = () => {
    const url = `${window.location.origin}/share/${public_id}/print`;
    window.open(url, "_blank"); // Opens in a new tab
  };

  const handleCreateDraft = () => {
    dispatch(createDraftCarePlan(carePlanId))
      .unwrap()
      .then(() => {
        setShowCreateDraftConfirm(false);
        setDraftStatus("success");
        setShowDraftStatus(true);
      })
      .catch(() => {
        setShowCreateDraftConfirm(false);
        setDraftStatus("failure");
        setShowDraftStatus(true);
      });
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Delete") {
      deleteTextField.setCustomValidity(
        'Please type "Delete" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }

    setDeleteText(""); //reset the delete text otherwise the next time the text box show Delete
    setShowDeleteConfirm(false);
    setDeleteText("");
    dispatch(deleteCarePlan({ carePlanId, type: "active" }));
    onClose(); // Close the overlay after deletion
  };

  // Floating menu items
  const menuItems = [
    {
      label: "Print plan",
      icon: <PublishIcon color="#004F96" />,
      action: handlePrint,
    },
    {
      label: "Create a draft",
      icon: <CreateDraftIcon color="#004F96" />,
      action: () => setShowCreateDraftConfirm(true),
    },
    {
      label: "Delete plan",
      icon: <DeleteIcon color="#004F96" />,
      action: () => setShowDeleteConfirm(true),
    },
  ];

  return (
    <>
      <FullScreenOverlay
        title={`Active Care Plan: ${nhsNumber}`}
        onClose={onClose}
        width="100%"
        height="100%"
        iconClose={true}
      >
        <FloatingMenu items={menuItems} position="bottom-center" />
        <FormSection>
          {sections
            .filter((section) =>
              section.items.some((item) => item.answer?.trim())
            )
            .map((section) => (
              <SectionContainer key={section.id}>
                <SectionTitleWrapper>
                  <Rectangle color={section.color} />
                  <h5 className="semi-bold neutral-900">{section.title}</h5>
                </SectionTitleWrapper>
                <Spacer height="16px" />

                {section.items.map((item) => (
                  <SectionCard key={item.id}>
                    <ItemTitle>
                      <h6 className="semi-bold neutral-900">{item.title}</h6>
                    </ItemTitle>
                    <ItemCard>
                      <ItemThumbnail
                        src={item?.image_url}
                        alt={item?.image_alt_text || item?.title}
                        width={"150px"}
                        height={"150px"}
                        border={"1px solid #e9ecef"}
                      />

                      <ItemContent>
                        <p className="body-text-regular regular neutral-700">
                          {item.description}
                        </p>
                        <AnswerText>
                          {item.answer || "No answer provided"}
                        </AnswerText>
                      </ItemContent>
                    </ItemCard>
                  </SectionCard>
                ))}

                <HrLine />
              </SectionContainer>
            ))}

          {/* Urgent Care Section */}
          <SectionContainer>
            <SectionTitleWrapper>
              <Rectangle color="#EC2D30" />
              <h5 className="semi-bold neutral-900">
                Important Information if I need urgent care
              </h5>
            </SectionTitleWrapper>
            <Spacer height="16px" />

            <UrgentCare
              retrievedContacts={contacts}
              mode="view"
              initialImportantInfo={important_info}
            />
          </SectionContainer>
        </FormSection>
      </FullScreenOverlay>

      {/* Confirmations for creating a draft and deleting the care plan */}
      {showCreateDraftConfirm && (
        <FullScreenOverlay
          width="480px"
          height="250px"
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowCreateDraftConfirm(false),
            },
            {
              label: "Yes, Create",
              onClick: handleCreateDraft,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Create draft care plan?</h5>
          <p>Are you sure you want to create a draft of this care plan?</p>
        </FullScreenOverlay>
      )}

      {showDeleteConfirm && (
        <FullScreenOverlay
          width="480px"
          height="300px"
          isPopup={true}
          popupType="delete"
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => {
                setShowDeleteConfirm(false);
                setDeleteText("");
              },
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDelete,
              variant: "danger",
              state: "idle",
            },
          ]}
        >
          <h5 className="semi-bold">Confirm Care Plan deletion</h5>
          <p>
            Are you sure you want to delete the
            <span className="confirm-delete-nhs"> Care Plan {nhsNumber}</span>?
            This action cannot be undone and all the data will be permanently
            removed.
          </p>
          <p>
            Type <b>"Delete"</b> to confirm
          </p>
          <form
            id="deleteConfirmationForm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              placeholder=""
              maxLength={10}
              showCharacterLimit={false}
              name="deleteText"
              value={deleteText}
              onChange={(e) => {
                setDeleteText(e.target.value); // Update state
                e.target.setCustomValidity(""); // Clear custom validation message
              }}
              autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
              required
            />
          </form>
        </FullScreenOverlay>
      )}

      {/* Draft creation status */}
      <DraftCreationStatus
        visible={showDraftStatus}
        status={draftStatus}
        nhsNumber={nhsNumber}
        onClose={() => setShowDraftStatus(false)}
      />
    </>
  );
};

export default ActivePlanAdminView;

/* --- Styled Components --- */

/* We'll wrap these 'FindOutMore' in <DigitalOnly> so that they do not print */

const FormSection = styled.div`
  width: 60%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const SectionCard = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-200, #f8f9fa);
`;

const SectionContainer = styled.div`
  margin-bottom: 32px;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const ItemTitle = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 24px;
  padding: 16px;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const AnswerText = styled.div`
  display: flex;
  // flex: 1 0 0;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #ced4da);
  min-height: 96px;

  /* Dynamic height handling  *******NOT GROWING TO 150 NEED TO CHECK******* */
  // max-height: 150px; /* Set the maximum height for the description */
  overflow-y: auto; /* Add a scrollbar if the content exceeds the height */
  word-wrap: break-word; /* Ensure long words are wrapped */
  line-height: 1.2; /* Adjust line height to provide better readability */

  /* Allow the element to grow up to the max-height */
  height: auto; /* Let the height grow based on content */
  // min-height: 96px; /* Set an initial minimum height */

  /* Optional: Scrollbar styling for better visibility */
  // scrollbar-width: thin; /* Firefox */
  // scrollbar-color: #ced4da transparent; /* Firefox */

  // &::-webkit-scrollbar {
  //   width: 6px;
  // }

  // &::-webkit-scrollbar-thumb {
  //   background-color: #ced4da; /* Scrollbar color */
  //   border-radius: 10px;
  // }

  // &::-webkit-scrollbar-track {
  //   background: transparent; /* Scrollbar track color */
  // }
`;

const Rectangle = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 100px;
  background: ${({ color }) => color || "#3a70e2"};
  width: 4px;
  max-width: 4px;
  max-height: 32px;
`;

const HrLine = styled.div`
  height: 1px;
  background-image: repeating-linear-gradient(
    to right,
    #ccc,
    #ccc 10px,
    /* Dash length of 5px */ transparent 5px,
    transparent 25px /* Gap of 5px */
  );
`;
